type PoolCreateValues = {
  location_id?: number | string;
  position_ids?: Array<number | string>;
  specialty_ids?: Array<number | string>;
  status?: 'active' | 'deleted';
  shift_needs?: string;
  pause_auto_updates?: boolean;
  requires_pro_review?: boolean;
};

const DEFAULT_ERROR_MESSAGE = 'Unable to create pool';

const isBlank = (value: unknown) => value === undefined || value === null || value === '';

const normalizeId = (value: number | string) => {
  if (typeof value === 'number') {
    return value;
  }
  return Number(value);
};

export const validatePoolCreateValues = (values: PoolCreateValues) => {
  const errors: Record<string, string> = {};

  if (isBlank(values?.location_id)) {
    errors.location_id = 'Required';
  }

  if (!Array.isArray(values?.position_ids) || values.position_ids.length === 0) {
    errors.position_ids = 'Required';
  }

  return errors;
};

export const buildPoolCreatePayload = (values: PoolCreateValues) => {
  const pool: Record<string, unknown> = {
    location_id: normalizeId(values.location_id as number | string),
    position_ids: (values.position_ids || []).map((id) => normalizeId(id)),
  };

  if (values.status) {
    pool.status = values.status;
  }

  if (values.shift_needs?.trim()) {
    pool.shift_needs = values.shift_needs.trim();
  }

  if (Array.isArray(values.specialty_ids) && values.specialty_ids.length > 0) {
    pool.specialty_ids = values.specialty_ids.map((id) => normalizeId(id));
  }

  if (typeof values.pause_auto_updates === 'boolean') {
    pool.pause_auto_updates = values.pause_auto_updates;
  }

  if (typeof values.requires_pro_review === 'boolean') {
    pool.requires_pro_review = values.requires_pro_review;
  }

  return { pool };
};

const fromErrorObject = (errors: unknown) => {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return '';
  }

  const parsed = Object.entries(errors).flatMap(([field, messages]) => {
    if (Array.isArray(messages)) {
      return messages.map((message) => `${field} ${message}`);
    }
    if (typeof messages === 'string') {
      return `${field} ${messages}`;
    }
    return [];
  });

  return parsed.join(', ');
};

export const getPoolCreateErrorMessage = (error: any) => {
  const errors = error?.response?.data?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(', ');
  }

  const errorsMessage = fromErrorObject(errors);
  if (errorsMessage) {
    return errorsMessage;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.message) {
    return error.message;
  }
  return DEFAULT_ERROR_MESSAGE;
};
